import React, { useCallback, useRef, useState } from "react";

/**
 * Accessible drag-and-drop file dropzone with thumbnail previews.
 * Calls onFiles(File[]) when files are dropped or selected.
 */
export function Dropzone({ accept = "image/*", multiple = true, disabled = false, onFiles, testid = "dropzone" }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback((fileList) => {
    if (!fileList) return;
    const files = Array.from(fileList).filter((f) => !accept || accept === "*" || f.type.startsWith(accept.replace("/*", "/")) || f.type.match(accept));
    if (files.length > 0) onFiles(files);
  }, [accept, onFiles]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer?.files);
  }, [handleFiles, disabled]);

  const onChange = useCallback((e) => {
    handleFiles(e.target.files);
    e.target.value = ""; // allow re-selecting same files
  }, [handleFiles]);

  return (
    <div
      data-testid={testid}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !disabled) inputRef.current?.click(); }}
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      className={`group cursor-pointer select-none border border-dashed transition-all duration-500
        px-6 py-12 md:py-16 text-center
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        ${over ? "border-ink-champagne bg-ink-champagne/[0.06]" : "border-ink-ivory/20 hover:border-ink-ivory/40"}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onChange}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <p className="text-[10px] tracking-[0.42em] uppercase text-ink-champagne">
        {over ? "Release to add" : "Drop photographs here"}
      </p>
      <p className="mt-3 text-sm text-ink-ivory/60">
        or click to choose files · JPG · PNG · WEBP · AVIF
      </p>
    </div>
  );
}
